import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Inbox,
  LayoutGrid,
  Loader2,
  LogOut,
  Search,
  Settings,
  User,
  UserRound,
  ChevronDown,
  X,
} from "lucide-react";
import { cn } from "@slumbr/ui";
import { searchUsers, signOut, useNotificationStore, useUserStore } from "@slumbr/core";
import type { PublicUserProfile } from "@slumbr/core";
import { useAppBadgesStore } from "@/stores/app-badges-store";
import { useOpenAppsStore } from "@/stores/open-apps-store";
import { usePinnedAppsStore } from "@/stores/pinned-apps-store";
import { usePresenceStore } from "@/stores/presence-store";
import { findAppById } from "./app-registry";
import { ProfileSwitcherMenu } from "@/shell/components/profile-switcher-menu";
import { DockInAppBar } from "@/shell/dock/dock-desktop";
import { unregisterPushSubscription } from "@/utils/web-notifications";
import type { PresenceEffectiveStatus, PresenceVisibility } from "@slumbr/core";

interface CommandItem {
  id: string;
  title: string;
  action: () => void;
}

const PRESENCE_OPTIONS: Array<{ value: PresenceVisibility; label: string; description: string }> = [
  { value: "online", label: "Online", description: "Show as active" },
  { value: "away", label: "Away", description: "Show as idle" },
  { value: "busy", label: "Busy", description: "Do not disturb" },
  { value: "offline", label: "Appear Offline", description: "Hide activity" },
];

function presenceColor(status?: PresenceEffectiveStatus) {
  if (status === "online") return "bg-emerald-500";
  if (status === "away") return "bg-amber-400";
  if (status === "busy") return "bg-[#ff477e]";
  return "bg-[#536471]";
}

export function TopBar() {
  const unreadCount = useNotificationStore((s) => s.unreadCount);
  const toggleNotifications = useNotificationStore((s) => s.toggleOpen);
  const user = useUserStore((s) => s.user);
  const logout = useUserStore((s) => s.logout);
  const openApps = useOpenAppsStore((s) => s.openApps);
  const activeAppId = useOpenAppsStore((s) => s.activeAppId);
  const switchApp = useOpenAppsStore((s) => s.switchApp);
  const closeApp = useOpenAppsStore((s) => s.closeApp);
  const badges = useAppBadgesStore((s) => s.badges);
  const clearBadge = useAppBadgesStore((s) => s.clearBadge);
  const pinnedApps = usePinnedAppsStore((s) => s.pinned);
  const togglePinApp = usePinnedAppsStore((s) => s.toggle);
  const [appContextMenu, setAppContextMenu] = useState<{ appId: string; x: number; y: number } | null>(null);
  const appContextMenuRef = useRef<HTMLDivElement>(null);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [menuRect, setMenuRect] = useState<DOMRect | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [userResults, setUserResults] = useState<PublicUserProfile[]>([]);
  const [isSearchingUsers, setIsSearchingUsers] = useState(false);
  const [presenceMenuOpen, setPresenceMenuOpen] = useState(false);
  const selfStatus = usePresenceStore((s) => s.selfStatus);
  const presenceVisibility = usePresenceStore((s) => s.visibility);
  const setPresenceVisibility = usePresenceStore((s) => s.setVisibility);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const orderedApps = useMemo(() => {
    return Array.from(openApps.values()).sort((a, b) => a.openedAt - b.openedAt);
  }, [openApps]);

  const pinnedNotOpen = useMemo(() => {
    const openIds = new Set(orderedApps.map((a) => a.appInfo.id));
    return pinnedApps
      .filter((id) => !openIds.has(id))
      .map((id) => findAppById(id))
      .filter((app): app is NonNullable<typeof app> => Boolean(app));
  }, [pinnedApps, orderedApps]);

  const commands: CommandItem[] = useMemo(() => [
    { id: "feed", title: "Go to Feed", action: () => navigate("/feed") },
    { id: "apps", title: "Open Apps", action: () => navigate("/home") },
    { id: "notes", title: "Go to Notes", action: () => navigate("/notes") },
    { id: "new-note", title: "Create New Note", action: () => navigate("/notes/new") },
    { id: "settings", title: "Open Settings", action: () => navigate("/settings") },
  ], [navigate]);

  const filteredCommands = query.trim()
    ? commands.filter((cmd) => cmd.title.toLowerCase().includes(query.toLowerCase()))
    : commands;

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (profileMenuOpen) {
        if (menuRef.current?.contains(event.target as Node)) return;
        if (buttonRef.current?.contains(event.target as Node)) return;
        setProfileMenuOpen(false);
      }
      if (searchOpen && !searchRef.current?.contains(event.target as Node)) {
        setSearchOpen(false);
      }
      if (appContextMenu && !appContextMenuRef.current?.contains(event.target as Node)) {
        setAppContextMenu(null);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setSearchOpen(true);
        requestAnimationFrame(() => inputRef.current?.focus());
      }
      if (event.key === "Escape") {
        setProfileMenuOpen(false);
        setSearchOpen(false);
        setAppContextMenu(null);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [profileMenuOpen, searchOpen, appContextMenu]);

  useEffect(() => {
    if (!searchOpen) return;
    requestAnimationFrame(() => inputRef.current?.focus());
  }, [searchOpen]);

  useEffect(() => {
    const trimmed = query.trim();
    if (!searchOpen || trimmed.length < 2) {
      setUserResults([]);
      setIsSearchingUsers(false);
      return;
    }

    let cancelled = false;
    setIsSearchingUsers(true);
    const timer = window.setTimeout(() => {
      searchUsers(trimmed)
        .then((response) => {
          if (!cancelled) setUserResults(response.items);
        })
        .catch(() => {
          if (!cancelled) setUserResults([]);
        })
        .finally(() => {
          if (!cancelled) setIsSearchingUsers(false);
        });
    }, 180);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [query, searchOpen]);

  const handleLogout = async () => {
    // Best-effort: remove this device's push subscription while we still
    // hold auth tokens (signOut clears them) so a signed-out browser stops
    // receiving pushes for this account.
    await unregisterPushSubscription().catch(() => {});
    await signOut();
    logout();
    setProfileMenuOpen(false);
    navigate("/login");
  };

  function runSearch() {
    const command = filteredCommands[0];
    const userResult = userResults[0];
    if (userResult) {
      navigate(`/profile/${userResult.userId}`);
      setSearchOpen(false);
      setQuery("");
      return;
    }
    if (command) {
      command.action();
      setSearchOpen(false);
      setQuery("");
    }
  }

  const profileHref = user?.id ? `/profile/${user.id}` : "/feed";
  const selectedPresence = PRESENCE_OPTIONS.find((option) => option.value === presenceVisibility) ?? PRESENCE_OPTIONS[0];
  const initials = (user?.displayName || user?.email || "U")
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <header className="relative hidden h-16 items-center border-b border-white/[0.05] bg-[#0b0d0f] px-4 md:flex">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <Link
          to="/feed"
          className="flex shrink-0 items-center rounded-lg px-1 text-[22px] font-black tracking-tight text-[#e0446e] transition hover:text-[#ff6b8f] focus:outline-none"
        >
          slumbr
        </Link>

        {/* One segmented control holds the apps grid and every open app, so
            the bar reads as a single deliberate control. Tabs size to their
            label (capped + truncated) — a fixed width left short names like
            "Chat" swimming in empty space. */}
        <nav className="flex min-w-0 items-center gap-1 rounded-xl bg-white/[0.035] p-1 ring-1 ring-inset ring-white/[0.05]">
          <button
            onClick={() => navigate("/home")}
            title="All apps"
            aria-label="All apps"
            className={cn(
              "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition",
              location.pathname === "/home"
                ? "bg-white/[0.1] text-[#e7e9ea] shadow-sm shadow-black/20"
                : "text-[#8b95a1] hover:bg-white/[0.05] hover:text-[#e7e9ea]"
            )}
          >
            <LayoutGrid className="h-[17px] w-[17px]" />
          </button>

          <span className="h-5 w-px shrink-0 bg-white/[0.07]" />

          <div className="flex min-w-0 items-center gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {orderedApps.map(({ appInfo: app, lastRoute }) => {
            const opened = openApps.get(app.id);
            const isActive = location.pathname !== "/home" && activeAppId === app.id;
            const isPinned = pinnedApps.includes(app.id);
            const badgeCount = badges[app.id] ?? 0;
            return (
              <AppTab
                key={app.id}
                name={app.name}
                Icon={app.icon}
                active={isActive}
                badgeCount={badgeCount}
                closable={!isPinned}
                onClick={() => {
                  clearBadge(app.id);
                  switchApp(app.id);
                  navigate(opened?.lastRoute ?? lastRoute);
                }}
                onContextMenu={(event) => {
                  event.preventDefault();
                  setAppContextMenu({ appId: app.id, x: event.clientX, y: event.clientY });
                }}
                onClose={() => {
                  closeApp(app.id);
                  if (isActive) navigate("/home");
                }}
              />
            );
          })}
          {pinnedNotOpen.map((app) => {
            const isActive = location.pathname.startsWith(app.route);
            return (
              <AppTab
                key={`pinned-${app.id}`}
                name={app.name}
                Icon={app.icon}
                active={isActive}
                badgeCount={badges[app.id] ?? 0}
                dimmed={(badges[app.id] ?? 0) === 0 && !isActive}
                onClick={() => navigate(app.route)}
                onContextMenu={(event) => {
                  event.preventDefault();
                  setAppContextMenu({ appId: app.id, x: event.clientX, y: event.clientY });
                }}
              />
            );
          })}
          </div>
        </nav>

        {appContextMenu && createPortal(
          <div
            ref={appContextMenuRef}
            className="fixed z-[100] min-w-44 overflow-hidden rounded-xl border border-white/[0.1] bg-[#101214] py-1 shadow-2xl"
            style={{ left: appContextMenu.x, top: appContextMenu.y }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => {
                togglePinApp(appContextMenu.appId);
                setAppContextMenu(null);
              }}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-semibold text-[#d7dbdc] transition hover:bg-white/[0.06]"
            >
              {pinnedApps.includes(appContextMenu.appId) ? "Unpin from app bar" : "Pin to app bar"}
            </button>
          </div>,
          document.body
        )}
      </div>

      <div className="ml-4 flex shrink-0 items-center gap-0.5 self-center">
        {/* Quick-actions dock, when the user has snapped it up here. */}
        <DockInAppBar />

        <div ref={searchRef} className="relative">
          <div className={cn(
            "flex h-11 items-center rounded-full border transition-all duration-300 ease-out",
            searchOpen
              ? "w-[360px] border-white/12 bg-[#101214] shadow-lg shadow-black/20"
              : "w-10 border-transparent bg-transparent hover:bg-surface-1 dark:hover:bg-surface-dark-1"
          )}>
            <button
              type="button"
              onClick={() => {
                if (!searchOpen) {
                  setSearchOpen(true);
                  requestAnimationFrame(() => inputRef.current?.focus());
                  return;
                }
                runSearch();
              }}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-gray-500 transition hover:text-[#ff6b8f]"
              aria-label="Search"
            >
              <Search className="h-5 w-5" />
            </button>
            <input
              ref={inputRef}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") runSearch();
              }}
              placeholder="Search or jump to..."
              className={cn(
                "h-full min-w-0 flex-1 bg-transparent pr-4 text-sm text-[#e7e9ea] outline-none placeholder:text-[#71767b] transition-all duration-200",
                searchOpen ? "opacity-100" : "pointer-events-none w-0 opacity-0"
              )}
            />
          </div>

          {searchOpen && (
            <div className="absolute right-0 top-full z-50 mt-2 w-[360px] overflow-hidden rounded-2xl border border-white/10 bg-[#101214] shadow-2xl shadow-black/45">
              <div className="max-h-96 overflow-auto p-2">
                {filteredCommands.length === 0 && userResults.length === 0 && !isSearchingUsers ? (
                  <p className="px-3 py-6 text-center text-sm text-[#71767b]">No results</p>
                ) : (
                  <>
                    {filteredCommands.length > 0 && (
                      <div className="mb-2">
                        <p className="px-3 pb-1 text-[11px] font-bold uppercase tracking-wide text-[#71767b]">Commands</p>
                        {filteredCommands.map((cmd) => (
                          <button
                            key={cmd.id}
                            className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm text-[#d7dbdc] transition hover:bg-white/[0.06]"
                            onClick={() => {
                              cmd.action();
                              setSearchOpen(false);
                              setQuery("");
                            }}
                          >
                            {cmd.title}
                          </button>
                        ))}
                      </div>
                    )}

                    {(userResults.length > 0 || isSearchingUsers) && (
                      <div>
                        <p className="px-3 pb-1 text-[11px] font-bold uppercase tracking-wide text-[#71767b]">People</p>
                        {isSearchingUsers && userResults.length === 0 ? (
                          <div className="flex items-center gap-2 px-3 py-2 text-sm text-[#71767b]">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Searching users...
                          </div>
                        ) : userResults.map((result) => (
                          <button
                            key={result.userId}
                            className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left transition hover:bg-white/[0.06]"
                            onClick={() => {
                              navigate(`/profile/${result.userId}`);
                              setSearchOpen(false);
                              setQuery("");
                            }}
                          >
                            <span className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full bg-brand-500 text-xs font-semibold text-white">
                              {result.avatarUrl ? <img src={result.avatarUrl} alt={result.displayName} className="h-full w-full object-cover" /> : <UserRound className="h-4 w-4" />}
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-sm font-semibold text-[#e7e9ea]">{result.displayName}</span>
                              <span className="block truncate text-xs text-[#71767b]">@{result.username}</span>
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        <button className="relative flex h-10 w-10 items-center justify-center rounded-full transition hover:bg-surface-1 dark:hover:bg-surface-dark-1" onClick={toggleNotifications}>
          <Inbox className="h-5 w-5" />
          {unreadCount > 0 && <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">{unreadCount > 99 ? "99+" : unreadCount}</span>}
        </button>

        <div className="relative">
          <button
            ref={buttonRef}
            onClick={() => {
              setMenuRect(buttonRef.current?.getBoundingClientRect() ?? null);
              setProfileMenuOpen((open) => !open);
            }}
            className="relative m-0.5 flex h-9 w-9 items-center justify-center overflow-hidden rounded-full bg-brand-500 text-sm font-semibold text-white shadow-sm ring-2 ring-brand-500/20 transition hover:scale-[1.04] hover:ring-brand-500/40"
            aria-label="Open profile menu"
            aria-expanded={profileMenuOpen}
          >
            {user?.avatar ? <img src={user.avatar} alt={user.displayName} className="h-full w-full object-cover" /> : initials}
            <span className={cn("absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-[#0b0d0f]", presenceColor(selfStatus?.effectiveStatus))} />
          </button>
          {profileMenuOpen && menuRect && createPortal(
            <div
              ref={menuRef}
              className="fixed z-[100] w-[300px] overflow-hidden rounded-3xl border border-white/[0.08] bg-[#0b0d0f]/95 text-[#e7e9ea] shadow-2xl shadow-black/70 backdrop-blur-xl"
              style={{ top: menuRect.bottom + 8, right: window.innerWidth - menuRect.right }}
            >
              <ProfileSwitcherMenu onClose={() => setProfileMenuOpen(false)} />

              <div className="px-2">
                <div className="h-px bg-gradient-to-r from-transparent via-white/[0.08] to-transparent" />
              </div>

              <div className="relative px-2 py-2">
                <button
                  type="button"
                  onClick={() => setPresenceMenuOpen((open) => !open)}
                  className="flex w-full items-center gap-3 rounded-2xl px-3 py-2 text-left transition hover:bg-white/[0.04]"
                >
                  <span className="relative flex h-7 w-7 items-center justify-center rounded-full bg-white/[0.04]">
                    <span className={cn("h-2 w-2 rounded-full shadow-[0_0_10px_currentColor]", presenceColor(presenceVisibility === "offline" ? "offline" : presenceVisibility))} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[10px] font-black uppercase tracking-[0.18em] text-[#536471]">Visibility</span>
                    <span className="block text-sm font-bold text-[#e7e9ea]">{selectedPresence.label}</span>
                  </span>
                  <ChevronDown className={cn("h-4 w-4 text-[#71767b] transition", presenceMenuOpen && "rotate-180 text-[#ff6b8f]")} />
                </button>
                {presenceMenuOpen && (
                  <div className="mt-1.5 space-y-0.5">
                    {PRESENCE_OPTIONS.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => {
                          void setPresenceVisibility(option.value);
                          setPresenceMenuOpen(false);
                        }}
                        className={cn(
                          "flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left transition hover:bg-white/[0.05]",
                          presenceVisibility === option.value && "bg-[#ff477e]/[0.08]"
                        )}
                      >
                        <span className={cn("h-2 w-2 rounded-full", presenceColor(option.value === "offline" ? "offline" : option.value))} />
                        <span className="min-w-0 flex-1">
                          <span className="block text-[13px] font-bold text-[#e7e9ea]">{option.label}</span>
                          <span className="block text-[11px] text-[#71767b]">{option.description}</span>
                        </span>
                        {presenceVisibility === option.value && (
                          <span className="text-[10px] font-black uppercase tracking-wide text-[#ff8fac]">Active</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="px-2">
                <div className="h-px bg-gradient-to-r from-transparent via-white/[0.08] to-transparent" />
              </div>

              <div className="space-y-0.5 p-2">
                <Link
                  to={profileHref}
                  onClick={() => setProfileMenuOpen(false)}
                  className="group flex items-center gap-3 rounded-2xl px-3 py-2.5 text-sm font-bold text-[#d7dbdc] transition hover:bg-white/[0.05] hover:text-[#e7e9ea]"
                >
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white/[0.04] text-[#ff6b8f] transition group-hover:bg-[#ff6b8f]/15">
                    <User className="h-4 w-4" />
                  </span>
                  View profile
                </Link>
                <Link
                  to="/settings"
                  onClick={() => setProfileMenuOpen(false)}
                  className="group flex items-center gap-3 rounded-2xl px-3 py-2.5 text-sm font-bold text-[#d7dbdc] transition hover:bg-white/[0.05] hover:text-[#e7e9ea]"
                >
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white/[0.04] text-[#71767b] transition group-hover:text-[#e7e9ea]">
                    <Settings className="h-4 w-4" />
                  </span>
                  Settings
                </Link>
              </div>

              <div className="border-t border-white/[0.06] p-2">
                <button
                  onClick={() => void handleLogout()}
                  className="group flex w-full items-center gap-3 rounded-2xl px-3 py-2.5 text-left text-sm font-bold text-red-300 transition hover:bg-red-500/10 hover:text-red-200"
                >
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-red-500/10 transition group-hover:bg-red-500/15">
                    <LogOut className="h-4 w-4" />
                  </span>
                  Log out
                </button>
              </div>
            </div>,
            document.body
          )}
        </div>
      </div>
    </header>
  );
}

/**
 * One segment in the app control. Active is a single signal — a lifted pill —
 * rather than a wash plus an underline plus a tinted icon all at once.
 */
function AppTab({
  name,
  Icon,
  active,
  badgeCount,
  closable,
  dimmed,
  onClick,
  onContextMenu,
  onClose,
}: {
  name: string;
  Icon: React.ComponentType<{ className?: string }>;
  active: boolean;
  badgeCount: number;
  closable?: boolean;
  dimmed?: boolean;
  onClick: () => void;
  onContextMenu: (event: React.MouseEvent) => void;
  onClose?: () => void;
}) {
  return (
    <button
      onClick={onClick}
      onContextMenu={onContextMenu}
      title={name}
      className={cn(
        "group relative flex h-8 min-w-0 max-w-[150px] shrink-0 items-center gap-2 rounded-lg pl-2.5 text-[13px] font-semibold tracking-tight transition-colors",
        closable ? "pr-1.5" : "pr-2.5",
        active
          ? "bg-white/[0.1] text-[#e7e9ea] shadow-sm shadow-black/20"
          : "text-[#8b95a1] hover:bg-white/[0.05] hover:text-[#e7e9ea]",
        dimmed && !active && "opacity-60 hover:opacity-100"
      )}
    >
      <span className="relative flex shrink-0 items-center">
        <Icon className={cn("h-[16px] w-[16px]", active && "text-[#ff8fac]")} />
        {badgeCount > 0 && !active && (
          <span className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-[#ff477e] ring-2 ring-[#0b0d0f]" />
        )}
      </span>

      <span className="min-w-0 truncate">{name}</span>

      {badgeCount > 0 && active && (
        <span className="shrink-0 rounded-full bg-[#ff477e] px-1.5 text-[10px] font-black tabular-nums text-white">
          {badgeCount > 99 ? "99+" : badgeCount}
        </span>
      )}

      {closable && onClose && (
        <span
          role="button"
          tabIndex={0}
          onClick={(event) => {
            event.stopPropagation();
            onClose();
          }}
          onKeyDown={(event) => {
            if (event.key !== "Enter" && event.key !== " ") return;
            event.preventDefault();
            event.stopPropagation();
            onClose();
          }}
          className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md text-[#71767b] opacity-0 transition hover:bg-white/[0.12] hover:text-[#e7e9ea] focus:opacity-100 group-hover:opacity-100"
          title={`Close ${name}`}
        >
          <X className="h-3 w-3" />
        </span>
      )}
    </button>
  );
}
